import React, { useState } from 'react';
import { postJSON } from '../lib/api';
import { getSessionId, markSurveySubmitted, markSurveySkipped } from '../lib/surveyStorage';

// Post-completion survey shown on the certificate page on first visit only.
//
// Five questions (six for the pilot cohort, which gets a bug-hunting Q6).
// All questions other than NPS are optional — completion rate beats coverage.
// The skip link sets a localStorage flag so the prompt never reappears.
//
// Cohort is derived server-side from request signals (review token vs not).
// After Stripe + auth land, the SPA will pass the user's resolved cohort so
// gift recipients, scholarship learners, and educators are tagged correctly.

const MODULE_OPTIONS = [
  { value: 'module-1', label: '1', long: 'What Is Generative AI' },
  { value: 'module-2', label: '2', long: 'AI in Everyday Life' },
  { value: 'module-3', label: '3', long: 'AI Risks' },
  { value: 'module-4', label: '4', long: 'Helping Kids Use AI' },
  { value: 'module-5', label: '5', long: 'AI in Family Life' },
  { value: 'module-6', label: '6', long: 'The Future of AI' },
];

const SHARE_OPTIONS = [
  { value: 'no_share', label: "Please don't share my answers" },
  { value: 'anonymous', label: 'Share anonymously' },
  { value: 'first_name', label: 'Share with my first name' },
];

function EyebrowLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] tracking-[0.18em] uppercase text-brand-aubergine mb-2"
    >
      {children}
    </label>
  );
}

// Pill buttons render with `role="radio"` so each set behaves as a proper
// radiogroup for screen readers. The parent wraps them in role="radiogroup"
// with an aria-label so the question is announced as the group label.
function PillButton({ active, onClick, children, ariaLabel }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      aria-label={ariaLabel}
      className={
        'min-w-[44px] h-11 px-3 rounded-full border text-sm font-medium transition-colors ' +
        (active
          ? 'bg-brand-deep text-bone border-brand-deep'
          : 'bg-cream/60 text-ink border-ink/15 hover:border-ink/40')
      }
    >
      {children}
    </button>
  );
}

export default function CertificateSurvey({ slug, isPilot, onDone }) {
  const [nps, setNps] = useState(null);
  const [oneSentence, setOneSentence] = useState('');
  const [changeAtHome, setChangeAtHome] = useState('');
  const [helpfulModule, setHelpfulModule] = useState('');
  const [oneThingToChange, setOneThingToChange] = useState('');
  const [pilotProblems, setPilotProblems] = useState('');
  const [shareConsent, setShareConsent] = useState('no_share');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function handleSkip() {
    markSurveySkipped(slug);
    if (onDone) onDone({ skipped: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (nps === null) {
      setError('Please pick a number from 0 to 10 for the first question.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await postJSON(`/api/surveys/${slug}`, {
        session_id: getSessionId(),
        nps_score: nps,
        one_sentence: oneSentence,
        change_at_home: changeAtHome,
        helpful_module: helpfulModule,
        one_thing_to_change: oneThingToChange,
        pilot_problems: isPilot ? pilotProblems : '',
        share_consent: shareConsent,
        first_name: shareConsent === 'first_name' ? firstName : '',
      });
      markSurveySubmitted(slug);
      if (onDone) onDone({ submitted: true });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto print:hidden">
      <div className="bg-white rounded-2xl border border-ink/10 shadow-soft px-7 sm:px-10 py-8 sm:py-10">
        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine">
          One quick step
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-ink mt-3 leading-tight">
          Two minutes before your certificate.
        </h1>
        <p className="mt-3 text-slate2 text-base leading-relaxed">
          Five short questions. Optional, but your answers shape the next
          version of this course.{' '}
          <button
            type="button"
            onClick={handleSkip}
            className="underline underline-offset-2 hover:text-ink"
          >
            Skip and view certificate
          </button>
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
          {/* Q1 — NPS */}
          <div>
            <EyebrowLabel>1 — Recommend</EyebrowLabel>
            <div className="text-ink mb-3" id="q-nps-label">
              How likely are you to recommend this course to another parent?
            </div>
            <div
              role="radiogroup"
              aria-labelledby="q-nps-label"
              className="flex flex-wrap gap-2"
            >
              {Array.from({ length: 11 }).map((_, i) => (
                <PillButton
                  key={i}
                  active={nps === i}
                  onClick={() => setNps(i)}
                  ariaLabel={`Score ${i}`}
                >
                  {i}
                </PillButton>
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate2 mt-2">
              <span>Not likely</span>
              <span>Extremely likely</span>
            </div>
          </div>

          {/* Q2 — one sentence */}
          <div>
            <EyebrowLabel htmlFor="q-one-sentence">2 — In your words</EyebrowLabel>
            <div className="text-ink mb-3">
              In one sentence, how would you describe this course to another parent?
            </div>
            <textarea
              id="q-one-sentence"
              value={oneSentence}
              onChange={(e) => setOneSentence(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-3 text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors"
              placeholder="A clear, calm guide for parents who feel behind on AI…"
            />
          </div>

          {/* Q3 — change at home */}
          <div>
            <EyebrowLabel htmlFor="q-change">3 — At home</EyebrowLabel>
            <div className="text-ink mb-3">
              Has anything changed at home since taking this course?
            </div>
            <textarea
              id="q-change"
              value={changeAtHome}
              onChange={(e) => setChangeAtHome(e.target.value)}
              rows={3}
              maxLength={1500}
              className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-3 text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors"
              placeholder="A new rule, a conversation, something you noticed…"
            />
          </div>

          {/* Q4 — helpful module */}
          <div>
            <EyebrowLabel>4 — Most helpful module</EyebrowLabel>
            <div className="text-ink mb-3" id="q-module-label">
              Which module helped you most?
            </div>
            <div
              role="radiogroup"
              aria-labelledby="q-module-label"
              className="flex flex-wrap gap-2"
            >
              {MODULE_OPTIONS.map((m) => (
                <PillButton
                  key={m.value}
                  active={helpfulModule === m.value}
                  onClick={() =>
                    setHelpfulModule(helpfulModule === m.value ? '' : m.value)
                  }
                  ariaLabel={`Module ${m.label}: ${m.long}`}
                >
                  {m.label}
                </PillButton>
              ))}
            </div>
            {helpfulModule ? (
              <div className="text-xs text-slate2 mt-2 italic">
                {MODULE_OPTIONS.find((m) => m.value === helpfulModule)?.long}
              </div>
            ) : null}
          </div>

          {/* Q5 — what to add or change */}
          <div>
            <EyebrowLabel htmlFor="q-change-add">5 — Add or change</EyebrowLabel>
            <div className="text-ink mb-3">
              What's one thing we should add or change?
            </div>
            <textarea
              id="q-change-add"
              value={oneThingToChange}
              onChange={(e) => setOneThingToChange(e.target.value)}
              rows={3}
              maxLength={1500}
              className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-3 text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors"
              placeholder="A topic, a format, a small fix…"
            />
          </div>

          {/* Q6 — pilot only: bug-hunting */}
          {isPilot ? (
            <div>
              <EyebrowLabel htmlFor="q-pilot">6 — Be ruthless</EyebrowLabel>
              <div className="text-ink mb-3">
                What confused you, broke, or felt off? You're piloting this — that's
                why we asked.
              </div>
              <textarea
                id="q-pilot"
                value={pilotProblems}
                onChange={(e) => setPilotProblems(e.target.value)}
                rows={3}
                maxLength={1500}
                className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-3 text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors"
                placeholder="A typo, a confusing lesson, a broken link, anything…"
              />
            </div>
          ) : null}

          {/* Share consent */}
          <div>
            <EyebrowLabel>Sharing</EyebrowLabel>
            <div className="text-ink mb-3">
              May we share your answers in marketing?
            </div>
            <div className="space-y-2">
              {SHARE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={
                    'flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ' +
                    (shareConsent === opt.value
                      ? 'border-brand-deep bg-cream'
                      : 'border-ink/15 bg-cream/40 hover:border-ink/30')
                  }
                >
                  <input
                    type="radio"
                    name="share-consent"
                    value={opt.value}
                    checked={shareConsent === opt.value}
                    onChange={() => setShareConsent(opt.value)}
                    className="accent-brand-deep"
                  />
                  <span className="text-ink text-sm">{opt.label}</span>
                </label>
              ))}
            </div>

            {shareConsent === 'first_name' ? (
              <div className="mt-3">
                <label
                  htmlFor="q-first-name"
                  className="block text-xs text-slate2 mb-1"
                >
                  First name to attribute (optional)
                </label>
                <input
                  id="q-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Alex"
                  className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-2.5 text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors"
                />
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3">
              {error}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting…' : 'Submit and view certificate'}
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-slate2 hover:text-ink underline underline-offset-2"
            >
              Skip and view certificate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
